import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { ImageUploader } from '../../components/ui/image-uploader';
import { 
  Loader2,
  LayoutTemplate,
  Package,
  DollarSign,
  Image as ImageIcon,
  Tag,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductEditorV2({ productId, onClose, onSave }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [pricingPreview, setPricingPreview] = useState(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    template_id: '',
    status: 'draft',
    is_featured: false,
    content: {
      short_description: '',
      full_description: ''
    },
    images: {
      primary: '',
      gallery: []
    },
    tags: [],
    seo: {
      title: '',
      description: ''
    }
  });

  const [newTag, setNewTag] = useState('');

  // Fetch templates on mount
  useEffect(() => {
    fetchTemplates();
    if (productId) {
      fetchProduct();
    }
  }, [productId]);

  // Fetch template details when template changes
  useEffect(() => {
    if (form.template_id) {
      fetchTemplateDetails(form.template_id);
    }
  }, [form.template_id]);

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/templates?status=active`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/v2/products/${productId}`);
      const data = await res.json();
      setForm({
        name: data.name || '',
        template_id: data.template_id || '',
        status: data.status || 'draft',
        is_featured: data.is_featured || false,
        content: data.content || { short_description: '', full_description: '' },
        images: data.images || { primary: '', gallery: [] },
        tags: data.tags || [],
        seo: data.seo || { title: '', description: '' }
      });
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateDetails = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}?include_options=true`);
      const data = await res.json();
      setSelectedTemplate(data);
      
      // Fetch pricing preview
      fetchPricingPreview(data);
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const fetchPricingPreview = async (template) => {
    if (!template?.pricing) return;
    
    try {
      let sampleConfig = {};
      if (template.pricing.model === 'per_sqft') {
        sampleConfig = { width: 48, height: 96, quantity: 1 };
      } else if (template.pricing.model === 'quantity_tiers') {
        sampleConfig = { quantity: template.pricing.quantity_tiers?.[0]?.qty || 100 };
      } else if (template.pricing.model === 'base_plus_options') {
        sampleConfig = { size: template.pricing.size_prices?.[0]?.id || 'sm', quantity: 1 };
      }

      const res = await fetch(`${API_URL}/api/pricing/v2/preview/template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pricing_config: template.pricing,
          sample_config: sampleConfig
        })
      });
      const data = await res.json();
      setPricingPreview(data);
    } catch (error) {
      console.error('Error fetching pricing preview:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = productId ? 'PUT' : 'POST';
      const url = productId 
        ? `${API_URL}/api/v2/products/${productId}`
        : `${API_URL}/api/v2/products`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        onSave();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (newTag && !form.tags.includes(newTag)) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // Group templates by type
  const templatesByType = templates.reduce((acc, template) => {
    const type = template.product_type || 'OTHER';
    if (!acc[type]) acc[type] = [];
    acc[type].push(template);
    return acc;
  }, {});

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {productId ? 'Edit Product' : 'Create New Product'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-3 gap-6">
            {/* Left side - Form */}
            <div className="col-span-2 space-y-6">
              {/* Template Selection */}
              <Card className={!form.template_id ? 'border-blue-500 bg-blue-50' : ''}>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <LayoutTemplate className="h-4 w-4" />
                    Select Template *
                  </CardTitle>
                  <CardDescription>
                    The template determines pricing, options, and specifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Select 
                    value={form.template_id} 
                    onValueChange={(v) => setForm(prev => ({ ...prev, template_id: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a product template..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(templatesByType).map(([type, typeTemplates]) => (
                        <div key={type}>
                          <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100">
                            {type === 'LGF' ? 'Large Format' : type === 'SMF' ? 'Small Format' : type === 'AWD' ? 'Awards' : type}
                          </div>
                          {typeTemplates.map(template => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name} ({template.code})
                            </SelectItem>
                          ))}
                        </div>
                      ))}
                    </SelectContent>
                  </Select>

                  {!form.template_id && (
                    <div className="mt-3 flex items-center gap-2 text-amber-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>Select a template to continue</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Product Details - Only show after template selected */}
              {form.template_id && (
                <>
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Product Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Product Name *</Label>
                          <Input 
                            value={form.name}
                            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Premium Vinyl Banner"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select 
                            value={form.status} 
                            onValueChange={(v) => setForm(prev => ({ ...prev, status: v }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Short Description</Label>
                        <Textarea 
                          value={form.content.short_description}
                          onChange={(e) => setForm(prev => ({ 
                            ...prev, 
                            content: { ...prev.content, short_description: e.target.value }
                          }))}
                          placeholder="Brief description for product listings"
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Full Description</Label>
                        <Textarea 
                          value={form.content.full_description}
                          onChange={(e) => setForm(prev => ({ 
                            ...prev, 
                            content: { ...prev.content, full_description: e.target.value }
                          }))}
                          placeholder="Detailed product description (supports markdown)"
                          rows={4}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={form.is_featured}
                            onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_featured: checked }))}
                          />
                          <Label>Featured Product</Label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Images */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Images
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Primary Image Upload */}
                      <div className="space-y-2">
                        <Label>Primary Image</Label>
                        <ImageUploader
                          value={form.images.primary}
                          onChange={(url) => setForm(prev => ({ 
                            ...prev, 
                            images: { ...prev.images, primary: url }
                          }))}
                          folder="products"
                          placeholder="Drop primary image here or click to upload"
                        />
                        {/* Also allow URL input */}
                        <Input 
                          value={form.images.primary}
                          onChange={(e) => setForm(prev => ({ 
                            ...prev, 
                            images: { ...prev.images, primary: e.target.value }
                          }))}
                          placeholder="Or paste image URL..."
                          className="text-sm"
                        />
                      </div>

                      {/* Gallery Images */}
                      <div className="space-y-2">
                        <Label>Gallery Images</Label>
                        <ImageUploader
                          value={form.images.gallery}
                          onChange={(urls) => setForm(prev => ({ 
                            ...prev, 
                            images: { ...prev.images, gallery: urls }
                          }))}
                          folder="products"
                          placeholder="Drop gallery images here"
                          multiple={true}
                          maxFiles={6}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Tags */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex gap-2">
                        <Input 
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          placeholder="Add a tag..."
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        />
                        <Button variant="outline" onClick={addTag}>Add</Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {form.tags.map(tag => (
                          <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => removeTag(tag)}>
                            {tag} ×
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Right side - Template Info & Pricing Preview */}
            <div className="col-span-1 space-y-4">
              {selectedTemplate ? (
                <>
                  {/* Template Info Card */}
                  <Card>
                    <CardHeader className="py-3">
                      <CardTitle className="text-sm">Template Info</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Template</span>
                        <span className="font-medium">{selectedTemplate.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type</span>
                        <Badge>{selectedTemplate.product_type}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Pricing Model</span>
                        <span className="font-medium">
                          {selectedTemplate.pricing?.model === 'per_sqft' ? 'Per Sq Ft' :
                           selectedTemplate.pricing?.model === 'quantity_tiers' ? 'Qty Tiers' :
                           'Base + Options'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Option Sets</span>
                        <span className="font-medium">{selectedTemplate.resolved_option_sets?.length || 0}</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pricing Preview */}
                  {pricingPreview && (
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          Pricing Preview
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {pricingPreview.scenarios?.slice(0, 4).map((scenario, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-2 border text-sm">
                              <span className="text-gray-600">{scenario.name}</span>
                              <span className="font-bold text-green-600">${scenario.retail || scenario.price}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                          Markup: {selectedTemplate.pricing?.default_markup_percent}%
                          {selectedTemplate.pricing?.fixed_addition > 0 && ` + $${selectedTemplate.pricing.fixed_addition}`}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Option Sets */}
                  {selectedTemplate.resolved_option_sets?.length > 0 && (
                    <Card>
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm">Available Options</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {selectedTemplate.resolved_option_sets.map(opt => (
                          <div key={opt.id} className="text-sm">
                            <span className="font-medium">{opt.name}</span>
                            <span className="text-gray-500 ml-2">({opt.choices?.length} choices)</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="bg-gray-50">
                  <CardContent className="py-12 text-center text-gray-500">
                    <LayoutTemplate className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>Select a template to see pricing and options</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.name || !form.template_id}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {productId ? 'Save Changes' : 'Create Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
